import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {UserData} from "types/User";
import UserForm from "pages/User/Form";
import {FormikHelpers} from "formik";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import type {AxiosError} from "axios";

function MutateUser() {
  const {id} = useParams();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const {data: user, isLoading} = useQuery(
    ['users', {id}],
    () => ngage.users.get(Number(id)),
    {enabled: auth.can('users:read') && !!id}
  )

  const store = useMutation((data: UserData) => ngage.users.store(data));
  const update = useMutation((data: UserData) => ngage.users.update(Number(id), data));

  // Prevent unauthorised mutating
  if (auth.cannot('users:write')) {
    return <NotAuthorised/>
  }

  // Handle form submission
  async function handleSubmit(values: UserData, {setSubmitting}: FormikHelpers<UserData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('users.operator', { refetchInactive: true })

    // If the current user has just updated their own account, refetch to update
    if (auth.user?.id === Number(id)) await auth.refetchCurrentUser();

    setSubmitting(false);
    navigate('/users')
  }

  // Handle store mutation
  async function handleStore(values: UserData) {
    try {
      await store.mutateAsync(values)
      notify("success", "User Registered", `${values.name} has been created`)
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      if (axiosError?.response?.data?.message === 'The email has already been taken.') {
        notify(
          'error',
          'User Not Updated',
          `${values.name} could not be updated because the email is already taken.`,
          5000
        );
      } else {
        notify("error", "User Not Registered", `${values.name} could not be stored`)
      }

      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: UserData) {
    try {
      await update.mutateAsync(values)
      notify("success", "User Updated", `${values.name} has been updated`)
      await invalidate(['users', {id}])
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;

      if (axiosError?.response?.data?.message === 'The email has already been taken.') {
        notify(
          'error',
          'User Not Updated',
          `${values.name} could not be updated because the email is already taken.`,
          5000
        );
      } else {
        notify("error", "User Not Updated", `${values.name} could not be updated`)
      }
      throw error
    }
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {'Admin': '/', 'Users': '/users', 'New User': '/users/new'}
  const editBreadcrumbs = {'Admin': '/', 'Users': '/users', 'Edit User': `/users/${id}/edit`}

  return (
    <Page
      title={id ? "Edit User" : "New User"}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <UserForm user={user} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateUser;